/**
 * Metadata Review Step Component - Phase 4 Implementation
 *
 * Allows users to review and edit AI-generated metadata before submission.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Button,
  Stack,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as ValidIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { LayerType } from '../../../../types/layerMetadata';
import { LayerMetadataEditor } from '../../../../components/common/LayerMetadataEditor';
import { useLayerValidation } from '../../hooks/useRegistrationFlow';
import { applyIntelligentLooksDefaults } from '../../../../components/common/LooksMetadataEditor';
import { applyIntelligentMovesDefaults } from '../../../../components/common/MovesMetadataEditor';
import { applyIntelligentWorldsDefaults } from '../../../../components/common/WorldsMetadataEditor';

export interface MetadataReviewStepProps {
  selectedLayer: LayerType | null;
  layerMetadata: any;
  isEditable?: boolean;
  onMetadataChange?: (metadata: any) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export const MetadataReviewStep: React.FC<MetadataReviewStepProps> = ({
  selectedLayer,
  layerMetadata,
  isEditable = true,
  onMetadataChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  const { validationState, validateMetadata, isValid, hasErrors, hasWarnings } =
    useLayerValidation(selectedLayer || undefined);

  // Apply intelligent defaults BEFORE validation for Looks, Moves, and Worlds layers
  // This ensures validation always sees metadata with defaults already applied
  const defaultedMetadata = useMemo(() => {
    if (!layerMetadata || !selectedLayer) {
      return layerMetadata;
    }

    // Handle nested metadata structure
    let metadataToProcess = layerMetadata;
    if (!metadataToProcess && (layerMetadata as any)?.data?.layerMetadata) {
      metadataToProcess = (layerMetadata as any).data.layerMetadata;
    }

    if (!metadataToProcess) {
      return layerMetadata;
    }

    // Apply layer-specific defaults
    switch (selectedLayer) {
      case 'L':
        return applyIntelligentLooksDefaults(metadataToProcess);
      case 'M':
        return applyIntelligentMovesDefaults(metadataToProcess);
      case 'W':
        return applyIntelligentWorldsDefaults(metadataToProcess);
      default:
        // For other layers (S, G, C, etc.), return as-is
        // Their defaults are handled in their respective editors
        return metadataToProcess;
    }
  }, [layerMetadata, selectedLayer]);

  // Validate defaulted metadata when it changes
  useEffect(() => {
    if (defaultedMetadata && selectedLayer) {
      validateMetadata(defaultedMetadata);
      
      // Sync defaulted metadata back to parent on initial mount/change
      // This ensures formData stays in sync with defaults applied
      if (onMetadataChange && (selectedLayer === 'L' || selectedLayer === 'M' || selectedLayer === 'W')) {
        // Only sync if defaults were actually applied (metadata changed)
        if (defaultedMetadata !== layerMetadata) {
          onMetadataChange(defaultedMetadata);
        }
      }
    }
  }, [defaultedMetadata, selectedLayer, validateMetadata, onMetadataChange, layerMetadata]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMetadataChange = useCallback(
    (updatedMetadata: any) => {
      // Apply defaults again if needed (for consistency)
      let processedMetadata = updatedMetadata;
      if (selectedLayer === 'L') {
        processedMetadata = applyIntelligentLooksDefaults(updatedMetadata);
      } else if (selectedLayer === 'M') {
        processedMetadata = applyIntelligentMovesDefaults(updatedMetadata);
      } else if (selectedLayer === 'W') {
        processedMetadata = applyIntelligentWorldsDefaults(updatedMetadata);
      }

      if (onMetadataChange) {
        // Pass the processed metadata back to parent to keep formData in sync
        onMetadataChange(processedMetadata);
      }
      // Re-validate after change
      validateMetadata(processedMetadata);
    },
    [onMetadataChange, validateMetadata, selectedLayer]
  );

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const getLayerDisplayName = (layer: LayerType | null): string => {
    const names: Record<LayerType, string> = {
      S: 'Stars',
      L: 'Looks',
      W: 'Worlds',
      M: 'Moves',
      G: 'Songs',
      C: 'Composite',
      T: 'Training Data',
      R: 'Rights',
      P: 'Personalize',
      B: 'Branded',
    };
    return layer ? names[layer] : 'Unknown';
  };

  const renderValidationSummary = () => {
    return (
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardHeader
          title="Validation Status"
          subheader="Review the generated metadata and validation results"
          action={
            <Stack direction="row" spacing={1}>
              {isValid && (
                <Chip
                  icon={<ValidIcon />}
                  label="Valid"
                  color="success"
                  variant="outlined"
                />
              )}
              {hasErrors && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${validationState.errors.length} Errors`}
                  color="error"
                  variant="outlined"
                />
              )}
              {hasWarnings && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${validationState.warnings.length} Warnings`}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
          }
        />
        <CardContent>
          {hasErrors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Validation Errors:</strong>
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationState.errors.map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {hasWarnings && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Validation Warnings:</strong>
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationState.warnings.map((warning, index) => (
                  <li key={index}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {isValid && !hasWarnings && (
            <Alert severity="success">
              <Typography variant="body2">
                All metadata fields are valid and complete. You can proceed to
                the next step.
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Required fields: {validationState.requiredFields.length} |
              Optional fields: {validationState.optionalFields.length}
            </Typography>

            {isEditable && (
              <Button
                variant={isEditMode ? 'contained' : 'outlined'}
                onClick={toggleEditMode}
                startIcon={isEditMode ? <ViewIcon /> : <EditIcon />}
              >
                {isEditMode ? 'View Mode' : 'Edit Mode'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderMetadataEditor = () => {
    if (!selectedLayer) {
      return (
        <Alert severity="info">
          No layer selected. Please complete the layer selection step first.
        </Alert>
      );
    }

    if (!defaultedMetadata) {
      return (
        <Alert severity="info">
          No metadata available to review. Please complete the file upload step to generate AI metadata first.
        </Alert>
      );
    }

    // Use defaultedMetadata instead of raw layerMetadata
    // This ensures the editor receives metadata with defaults already applied
    return (
      <LayerMetadataEditor
        layer={selectedLayer}
        layerMetadata={defaultedMetadata}
        isEditable={isEditable && isEditMode}
        onMetadataChange={handleMetadataChange}
        showProcessingFeedback={false}
      />
    );
  };

  const renderRawData = () => {
    return (
      <Card elevation={1}>
        <CardHeader
          title="Raw Metadata"
          subheader="JSON representation of the generated metadata"
        />
        <CardContent>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 400,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
            }}
          >
            {JSON.stringify(defaultedMetadata || layerMetadata, null, 2)}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Review {getLayerDisplayName(selectedLayer)} Metadata
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review the AI-generated metadata and make any necessary edits before
        proceeding to final submission.
      </Typography>

      {renderValidationSummary()}

      <Card elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Metadata Editor" />
            <Tab label="Raw Data" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {renderMetadataEditor()}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {renderRawData()}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default MetadataReviewStep;
